/**
 * Scroll button for the slideshow thumbnails rail.
 *
 * The rail (`.slideshow-controls__thumbnails-container`) is a plain overflow container, so on a
 * product with more media than fits the column there is nothing telling a customer the rest is
 * there. One of these renders above the rail and one below it, each paging the rail a screenful at
 * a time in its own direction.
 *
 * Both stay in the layout while the rail overflows and only disable at their extreme, because
 * removing one would change the rail's height and shift the thumbnails under the cursor.
 *
 * Deliberately extends HTMLElement rather than @theme/component: it drives a sibling element it
 * does not own, and has no refs or state the theme's morph machinery needs to reconcile.
 */
export class GalleryThumbnailsScroll extends HTMLElement {
  /** @type {HTMLElement | null} */
  #rail = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#rail = this.closest('slideshow-controls')?.querySelector('.slideshow-controls__thumbnails-container') ?? null;

    if (!this.#rail) return;

    this.addEventListener('click', this.#handleClick);
    this.#rail.addEventListener('scroll', this.#sync, { passive: true });

    // The rail only overflows at certain viewport widths, and its scroll height changes as the
    // thumbnail images load, so both the rail and this element are watched.
    this.#resizeObserver = new ResizeObserver(this.#sync);
    this.#resizeObserver.observe(this.#rail);

    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#rail?.removeEventListener('scroll', this.#sync);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#rail = null;
  }

  #handleClick = () => {
    const rail = this.#rail;

    if (!rail || this.#button?.disabled) return;

    // Leave a thumbnail's worth of overlap between pages so the rail never jumps a full screen of
    // media past what the customer was looking at.
    const overlap = rail.querySelector('.slideshow-controls__thumbnail')?.clientHeight ?? 0;
    const page = Math.max(rail.clientHeight - overlap, rail.clientHeight * 0.5);

    rail.scrollBy({ top: page * this.#step, behavior: 'smooth' });
  };

  #sync = () => {
    const rail = this.#rail;

    if (!rail) return;

    // A sub-pixel scroll height is reported on fractional zoom levels, so require a real gap
    // before showing the control.
    const scrollable = rail.scrollHeight - rail.clientHeight > 1;

    this.toggleAttribute('hidden', !scrollable);

    const button = this.#button;

    if (button) button.disabled = !scrollable || this.#atLimit;
  };

  /** @returns {HTMLButtonElement | null} */
  get #button() {
    return this.querySelector('button');
  }

  /** Scroll direction as a multiplier: -1 pages towards the top, 1 towards the end. */
  get #step() {
    return this.getAttribute('direction') === 'up' ? -1 : 1;
  }

  /** Whether the rail has run out of room in this button's direction. */
  get #atLimit() {
    const rail = this.#rail;

    if (!rail) return true;

    if (this.#step < 0) return rail.scrollTop <= 1;

    return Math.ceil(rail.scrollTop + rail.clientHeight) >= rail.scrollHeight - 1;
  }
}

if (!customElements.get('gallery-thumbnails-scroll')) {
  customElements.define('gallery-thumbnails-scroll', GalleryThumbnailsScroll);
}
